import React, { useState } from 'react';
import { useSignMessage } from 'wagmi';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import { ShareToSocialModal } from '@components/modals/CommentV2/ShareToSocial';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import {
  NftCommentDocument,
  NftInitialCommentDocument,
  PublishCommentDocument,
  ReleasePageDocument,
  SelectCommentSongSlotDocument,
  setQueryData,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useConnectedWallet } from '@hooks/useConnectedWallet';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { useNftOwnershipData } from '@hooks/useNftOwnershipData';
import type { UserNft } from './useMultipleNftsForRelease';

const pillar = PILLARS.MINTING;
const isArtistOnlyPage = false;

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation publishComment($nftId: UUID!, $message: String!, $signature: String!) {
    setComment(nftId: $nftId, message: $message, signature: $signature) {
      id
      comment {
        id
        message
      }
      releaseCollector: collectorRelease {
        id
      }
      release {
        id
        ...ShareToSocialModalReleaseInfo
      }
      ...ShareToSocialModalNftInfo
    }
  }

  mutation selectCommentSongSlot($songSlot: Int!, $nftId: UUID!) {
    selectSongSlotUsingNftId(songSlot: $songSlot, nftId: $nftId) {
      __typename
      ... on MutationSelectSongSlotUsingNftIdSuccess {
        data {
          id
          songSlot
        }
      }
      ... on Error {
        message
      }
    }
  }

  query nftComment($nftId: UUID!) {
    nftFromId(id: $nftId) {
      id
      comment {
        id
        message
      }
    }
  }
`);

export function usePublishComment({
  releaseId,
  selectedNft,
  songSlot,
}: {
  releaseId: string;
  selectedNft?: UserNft;
  songSlot: number | null;
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  const { openModal } = ModalContext.useContainer();
  const { openToast, closeToast } = ToastContext.useContainer();
  const { signMessageAsync } = useSignMessage();
  const { getDeepLink, isAuthenticating } = useConnectedAuth();
  const logError = useLogError();
  const logInfo = useLogInfo();

  const { nftOwnedByUser, currentComment } = useNftOwnershipData({ releaseId });

  const selectedNftId = selectedNft?.id;
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: nftInitialCommentData, isInitialLoading: isInitialCommentLoading } = useQuery(
    NftCommentDocument,
    {
      variables: !!selectedNftId && { nftId: selectedNftId },
      staleTime: 0,
    },
  );

  const { mutateAsync: selectSongSlotUsingNftId } = useMutation(SelectCommentSongSlotDocument, {
    retry: 5,
  });

  const { mutateAsync: setCommentMutation, isLoading: commentMutationLoading } = useMutation(
    PublishCommentDocument,
    {
      retry: 5,
      onSuccess({
        data: {
          setComment: { id, comment },
        },
      }) {
        setQueryData(
          {
            query: NftInitialCommentDocument,
            variables: { nftId: id },
          },
          {
            data: {
              nftFromId: {
                id,
                comment,
              },
            },
          },
        );
      },
    },
  );

  const isLoading =
    isInitialCommentLoading || commentMutationLoading || isAuthenticating || isPublishing;

  const nftInitialCommentDataMessage = selectedNftId
    ? nftInitialCommentData?.data.nftFromId?.comment?.message ||
      (nftOwnedByUser?.id === selectedNftId ? currentComment : undefined)
    : undefined;

  const [commentText, setCommentText] = React.useState(nftInitialCommentDataMessage || '');

  useSafeLayoutEffect(() => {
    setCommentText(nftInitialCommentDataMessage ?? '');
  }, [nftInitialCommentDataMessage]);

  const nftToEditId = selectedNftId || nftOwnedByUser?.id;

  const handlePublishCommentMutation = useStableCallback(async () => {
    if (!nftToEditId) return;

    let signature: string | undefined;
    let deepLink: string | null = null;
    try {
      openToast({
        text: 'Please approve the signature request in your wallet.',
      });
      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }
      signature = await signMessageAsync({
        message: commentText,
      });
    } catch (err) {
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_ERROR,
        error: err,
        errorType: ERROR_TYPE.SIGN_MESSAGE_ERROR,
        message: 'Error signing message',
        level: 'fatal',
        pillar,
        toast: 'Something went wrong. Please try again.',
        isArtistOnlyPage,
        indexedTags: {
          releaseId,
          nftId: selectedNftId,
        },
        unindexedExtra: {
          deepLink, // this helps us know if users was going through the mobile deep linking flow or not
          commentText,
        },
      });

      return;
    }

    if (!signature) {
      const message = 'No signature';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_SIGNATURE,
        message: 'Error signing message',
        level: 'fatal',
        pillar,
        toast: 'Something went wrong. Please try again.',
        isArtistOnlyPage,
        indexedTags: {
          releaseId,
          nftId: selectedNftId,
        },
        unindexedExtra: {
          deepLink,
          commentText,
        },
      });
      return;
    }

    openToast({
      text: <p className="loading-ellipsis">Setting your comment</p>,
    });

    try {
      setIsPublishing(true);
      const response = await setCommentMutation(
        {
          signature,
          nftId: nftToEditId,
          message: commentText,
        },
        {},
      );
      if (!!response.data?.setComment) {
        openModal(ModalType.SHARE_TO_SOCIAL, {
          body: <ShareToSocialModal releaseId={releaseId} nftId={nftToEditId} />,
        });
        logInfo({
          action: MINTING_INFO_ACTIONS.COMMENT_SET,
          message: `User set comment on NFT ${selectedNftId}`,
          pillar,
          isArtistOnlyPage,
          data: {
            nftId: nftToEditId,
            commentText,
            releaseId,
          },
        });
      } else {
        openToast({
          text: 'Something went wrong. Please try again',
          variant: 'error',
        });
      }
      closeToast();
    } catch (err) {
      closeToast();
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_ERROR,
        error: err,
        errorType: ERROR_TYPE.UNKNOWN,
        message: 'Error signing message',
        level: 'fatal',
        pillar,
        toast: 'Encountered an error. Please try again.',
        isArtistOnlyPage,
        indexedTags: {
          releaseId,
          nftId: selectedNftId,
        },
        unindexedExtra: {
          commentText,
        },
      });
    }
  });

  const handlePublishComment = useConnectedWallet({
    onAuth: handlePublishCommentMutation,
  });

  const publishComment = async () => {
    if (songSlot === undefined || songSlot === null) {
      openToast({
        text: 'Please select a spot on the song to leave your comment',
        variant: 'error',
      });
      return;
    }

    if (!isAuthenticatedWithAPI) {
      openToast({
        text: 'Please Sign in',
        variant: 'error',
      });
      return;
    }

    if (!selectedNftId) {
      const message = 'Error selecting timeslot, no nftId';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_NFT_ID,
        message,
        toast: 'Please refresh and try again',
        level: 'fatal',
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          releaseId,
          songSlot,
        },
      });
      return;
    }

    if (commentText.trim().length === 0) {
      openToast({
        text: 'Comments cannot be blank. Please try again.',
        variant: 'error',
      });
      return;
    }

    try {
      if (typeof selectedNft.songSlot !== 'number') {
        // If we have the NFT id, we can just update the song slot
        const { errors, data } = await selectSongSlotUsingNftId(
          { nftId: selectedNftId, songSlot: songSlot },
          {},
        );

        if (
          data?.selectSongSlotUsingNftId.__typename === 'MutationSelectSongSlotUsingNftIdSuccess'
        ) {
          logInfo({
            action: MINTING_INFO_ACTIONS.COMMENT_TIMESLOT_SELECTED,
            message: 'Comment timeslot selected',
            pillar,
            isArtistOnlyPage,
            data: {
              releaseId,
              nftId: selectedNftId,
              songSlot,
            },
          });
          await handlePublishComment();
        } else if (data?.selectSongSlotUsingNftId.__typename === 'UniqueConstraintError') {
          const message = 'Unique constraint error';
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: new Error(message),
            errorType: ERROR_TYPE.UNIQUE_CONSTRAINT_ERROR,
            message,
            toast: 'Comment slot taken! Please select another.',
            level: 'info',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId: selectedNftId,
              songSlot,
            },
          });
        } else if (typeof data?.selectSongSlotUsingNftId.__typename === 'string') {
          const message = data.selectSongSlotUsingNftId.message;
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: new Error(message),
            errorType: ERROR_TYPE.UNKNOWN,
            message,
            toast: message,
            level: 'info',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId: selectedNftId,
              songSlot,
            },
          });
        } else if (errors?.length) {
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: errors[0], // this comes from apollo errors
            errorType: ERROR_TYPE.UNKNOWN,
            toast: 'Something went wrong',
            message: 'Unknown error during selectSongSlotUsingNftId mutation',
            level: 'fatal',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId: selectedNftId,
              songSlot,
            },
          });
        } else {
          const message =
            'SetTimeslot has no errors, but also a no success state. This should never happen.';
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: new Error(message),
            errorType: ERROR_TYPE.UNKNOWN,
            toast: 'Something went wrong',
            message,
            level: 'fatal',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId: selectedNftId,
              songSlot,
            },
          });
        }
      } else {
        await handlePublishComment();
      }
    } catch (error) {
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error,
        errorType: ERROR_TYPE.UNKNOWN,
        toast: 'Something went wrong',
        message: 'Unknown error setting comment timeslot',
        level: 'fatal',
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          releaseId,
          nftId: selectedNftId,
          songSlot,
        },
      });
    }

    setIsPublishing(false);
  };

  return {
    publishComment,
    isLoading,
    commentText,
    setCommentText,
    isPublishing,
  };
}

RefetchOperationsOnComplete({
  trigger: [PublishCommentDocument],
  refetch: [ReleasePageDocument],
});
